import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import { Environment } from '~/engine/data/Environment';

interface UsePreviewBaseOptions {
    environment: Environment | null;
}

export interface PreviewBaseResult {
    isPreviewing: boolean;
    setIsPreviewing: Dispatch<SetStateAction<boolean>>;
    abortRef: React.MutableRefObject<AbortController | null>;
    updateView: () => void;
    clearPreview: () => void;
    cleanup: () => void;
}

/**
 * Shared base for SDF-operation preview hooks.
 *
 * Provides the view-update, preview-clear, abort, and cleanup primitives that
 * are identical across all operation-specific preview hooks (margin, ring, …).
 * Each concrete hook calls this base and adds its own operation handler on top.
 */
export function usePreviewBase({
    environment,
}: UsePreviewBaseOptions): PreviewBaseResult {
    const [isPreviewing, setIsPreviewing] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    const updateView = useCallback(() => {
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                environment?.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }, [environment?.structureSet]);

    const clearPreview = useCallback(() => {
        if (environment?.operationPreviewStructure) {
            environment.operationPreviewStructure = undefined;
            updateView();
        }
    }, [environment, updateView]);

    const cleanup = useCallback(() => {
        abortRef.current?.abort();
        clearPreview();
    }, [clearPreview]);

    return {
        isPreviewing,
        setIsPreviewing,
        abortRef,
        updateView,
        clearPreview,
        cleanup,
    };
}
